import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { Input } from "@/components/ui/Input";
import { cn } from "@/lib/utils";

import { DeferredCommitFieldFrame } from "./DeferredCommitFieldFrame";
import {
  deferredCommitControlClassName,
  deferredCommitOverlayGlyphPadding,
  useDeferredCommitFieldFeedback,
} from "./deferredCommitFieldShared";
import { deferredCommitStatusGlyphVisible } from "./DeferredCommitStatusGlyph";

function normalizeDeferredCommitText(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/** Local draft text field; commits on blur or Enter so table edits do not save on every keystroke. */
export function DeferredCommitTextInput({
  value,
  disabled,
  pending,
  commitError,
  commitSucceeded,
  statusGlyphClassName,
  className,
  placeholder,
  label,
  onCommit,
}: {
  value: string;
  disabled: boolean;
  pending?: boolean;
  commitError?: string | null;
  commitSucceeded?: boolean;
  statusGlyphClassName?: string;
  className?: string;
  placeholder?: string;
  label?: ReactNode;
  onCommit: (value: string | null) => void;
}): JSX.Element {
  const [draft, setDraft] = useState(value);
  const skipNextBlurCommitRef = useRef(false);
  useEffect(() => {
    setDraft(value);
  }, [value]);

  const isDirty = useMemo(() => {
    return normalizeDeferredCommitText(draft) !== normalizeDeferredCommitText(value);
  }, [draft, value]);

  const flashTone = useDeferredCommitFieldFeedback(commitSucceeded, commitError);
  const showOverlayGlyph = deferredCommitStatusGlyphVisible({
    pending,
    isDirty,
    commitError,
  });

  const commitDraft = useCallback((): void => {
    if (pending === true) {
      return;
    }
    const next = normalizeDeferredCommitText(draft);
    const current = normalizeDeferredCommitText(value);
    if (next !== current) {
      onCommit(next);
    }
  }, [draft, onCommit, pending, value]);

  return (
    <DeferredCommitFieldFrame
      label={label}
      pending={pending}
      isDirty={isDirty}
      disabled={disabled}
      commitError={commitError}
      commitSucceeded={commitSucceeded}
      statusGlyphClassName={statusGlyphClassName}
      showOverlayGlyph={showOverlayGlyph}
    >
      <Input
        type="text"
        value={draft}
        disabled={disabled}
        placeholder={placeholder}
        aria-busy={pending === true}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        onBlur={() => {
          if (skipNextBlurCommitRef.current) {
            skipNextBlurCommitRef.current = false;
            return;
          }
          commitDraft();
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            skipNextBlurCommitRef.current = true;
            commitDraft();
            event.currentTarget.blur();
          }
        }}
        className={cn(
          "min-w-0 w-full",
          deferredCommitOverlayGlyphPadding(showOverlayGlyph && !label),
          className,
          deferredCommitControlClassName({
            isDirty,
            disabled,
            pending,
            commitError,
            flashTone,
          }),
        )}
      />
    </DeferredCommitFieldFrame>
  );
}
